import {
	getCookie,
	getSystemTheme,
	setCookie,
	THEME_COOKIE_NAME,
	type Theme,
} from "@designs-by-maryam/ui/lib/cookies";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type ThemeContextType = {
	theme: Theme;
	resolvedTheme: "light" | "dark";
	setTheme: (theme: Theme) => void;
	toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [theme, setThemeState] = useState<Theme>("dark");
	const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("dark");

	// Initialize theme from cookie or default to dark
	useEffect(() => {
		const stored = getCookie(THEME_COOKIE_NAME) as Theme | null;
		if (stored && ["light", "dark", "system"].includes(stored)) {
			setThemeState(stored);
		}
	}, []);

	// Resolve the actual theme and apply to document
	useEffect(() => {
		const resolved = theme === "system" ? getSystemTheme() : theme;
		setResolvedTheme(resolved);

		const root = document.documentElement;
		root.classList.remove("light", "dark");
		root.classList.add(resolved);
	}, [theme]);

	// Listen for system theme changes
	useEffect(() => {
		if (theme !== "system") {
			return;
		}

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = () => {
			setResolvedTheme(getSystemTheme());
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [theme]);

	const setTheme = useCallback((newTheme: Theme) => {
		setThemeState(newTheme);
		setCookie(THEME_COOKIE_NAME, newTheme);
	}, []);

	const toggleTheme = useCallback(() => {
		setTheme(resolvedTheme === "dark" ? "light" : "dark");
	}, [resolvedTheme, setTheme]);

	return (
		<ThemeContext.Provider
			value={{ theme, resolvedTheme, setTheme, toggleTheme }}
		>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
